import React, { memo, useEffect, useState } from 'react';
import * as Styled from './styled';
import * as GStyled from '../../../../styles/components';
import { RuleBuilderInput, RuleBuilderInputValue } from '..';
import { ReportField, ReportFieldType } from '../../../../models/reportField';
import { ReportIncludedFieldFilterOperator } from '../../../../models';
import {
  LOGICAL_RULE_SELECT_OPTIONS,
  COMPARISON_RULE_SELECT_OPTIONS,
} from '../../../../constants';
import { SelectOption } from '@leasequery/lq.react-component-library';
import { fetchReportFieldDropDownOptionsWithCaches } from '../../../../api';
import { useAuth } from '../../../../context';
import { fillMissingLabelForDropdownInput } from './helpers';
import { Icon } from '../../../../components';
import { formatDateByLocale } from '../../../../util/dateHelpers';

const buildDisplayedValue = (
  reportField: ReportField,
  value: RuleBuilderInputValue,
  dropdownFieldValues: Map<number, SelectOption<number>>,
  userLocale: string,
): JSX.Element[] => {
  if (reportField.fieldType === ReportFieldType.Date && userLocale && value) {
    const formattedDate = formatDateByLocale(new Date(value as string), userLocale);
    return [<div key={value as string}><>{formattedDate}</></div>];
  }
  if (reportField.fieldType !== ReportFieldType.DropDown)
    return [<div key={value as string}><>{value}</></div>];

  return (value as SelectOption<number>[]).map((opt) => {
    const dropdownOption = dropdownFieldValues
      ? dropdownFieldValues.get(opt.value)
      : null;
    return (
      <Styled.RuleValue key={opt.value}>
        {dropdownOption ? dropdownOption.label : opt.label}
      </Styled.RuleValue>
    );
  });
};

interface RuleInfoProps {
  reportField: ReportField;
  rule: RuleBuilderInput;
  hasLogicalRule: boolean;
  standardReportId?: number;
  onRemoveClick?: () => void;
  onEditClick?: () => void;
  onRuleChange?: (rule: RuleBuilderInput) => void;
}

function RuleInfo({
  reportField,
  rule,
  hasLogicalRule,
  standardReportId,
  onRemoveClick,
  onEditClick,
  onRuleChange,
}: RuleInfoProps): JSX.Element {
  const { client, user } = useAuth();
  const [dropdownFieldValuesMap, setDropdownFieldValuesMap] = useState<
    Map<number, SelectOption<number>>
  >(new Map());
  const logicalRule = LOGICAL_RULE_SELECT_OPTIONS[rule.logicalRule];
  const comparisonRule =
    COMPARISON_RULE_SELECT_OPTIONS[reportField.fieldType][rule.comparisonRule];
  const hasValue2Column =
    rule.comparisonRule === ReportIncludedFieldFilterOperator.between;

  useEffect(() => {
    if (reportField.fieldType !== ReportFieldType.DropDown) return;
    const fetchData = async (): Promise<void> => {
      const dropdownOptions = await fetchReportFieldDropDownOptionsWithCaches(
        reportField.id,
        client.clientId,
        reportField.isClientCustom,
        reportField.isAllocation,
        reportField.isDimension,
        reportField.isDimensionItemId,
        standardReportId,
      );
      const dropDownFieldValues = dropdownOptions.reduce(
        (acc, val) => acc.set(val.value, val),
        new Map<number, SelectOption<number>>(),
      );
      setDropdownFieldValuesMap(dropDownFieldValues);

      // Check and fill missing label for inputted dropdown options from fetched data
      const { isUpdated, rule: nextRule } = fillMissingLabelForDropdownInput(
        rule,
        dropDownFieldValues,
      );
      if (isUpdated) {
        // There some option's labels were updated.
        // Then raise the change event for further actions like updating the default data before running report
        onRuleChange(nextRule);
      }
    };
    fetchData();
  }, [
    client.clientId,
    reportField.id,
    reportField.fieldType,
    reportField.isAllocation,
    standardReportId,
    reportField.isClientCustom,
    rule,
    onRuleChange,
    reportField.isDimension,
    reportField.isDimensionItemId,
  ]);

  return (
    <Styled.RuleInfo
      hasLogicalColumn={hasLogicalRule}
      hasValue2Column={hasValue2Column}
    >
      {hasLogicalRule && <span>{logicalRule && logicalRule.label}</span>}
      <span>{comparisonRule && comparisonRule.label}</span>
      <span>
        {buildDisplayedValue(
          reportField,
          rule.value1,
          dropdownFieldValuesMap,
          user.localePreference,
        )}
      </span>
      {hasValue2Column && (
        <>
          <span>And</span>
          <span>
            {buildDisplayedValue(
              reportField,
              rule.value2,
              dropdownFieldValuesMap,
              user.localePreference,
            )}
          </span>
        </>
      )}
      <GStyled.FlatButton onClick={onEditClick}>
        <Icon name="edit" />
      </GStyled.FlatButton>
      <GStyled.FlatButton onClick={onRemoveClick}>
        <Icon name="times" />
      </GStyled.FlatButton>
    </Styled.RuleInfo>
  );
}

export default memo(RuleInfo);
